import { useState } from 'react';
import type { ValidationErrors } from '../../../utils/validation';
import { desktopProcessAccessReasonSchema } from '../../../../common/validation/desktopProcessAccessReason';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { Button, NullableInputText } from '../../elements/common';
import { FormLabel } from '../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../elements/forms/InputContainer';
import { Modal } from '../../elements/modals/Modal';

interface IDesktopProcessAccessReasonsProps {
    onClose: () => Promise<void>;
    onReasonGiven: () => Promise<void>;
    organizationId?: string;
}

export function DesktopProcessAccessReasonsModal(props: IDesktopProcessAccessReasonsProps) {
    const [reasonForAccess, setReasonForAccess] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(emptyValidationErrors);

    return (
        <>
            {props.organizationId && (
                <Modal
                    actionButtons={[
                        <Button
                            color={'secondary'}
                            disabled={isSaving}
                            large
                            onClick={async () => await props.onClose()}
                            outlined
                        >
                            {translateKey('common.cancel')}
                        </Button>,
                        <Button
                            color={'secondary'}
                            disabled={reasonForAccess === null || isSaving}
                            large
                            onClick={async () => await onSubmit()}
                        >
                            {translateKey('common.save')}
                        </Button>,
                    ]}
                    isOpen
                    onClose={async () => await props.onClose()}
                    size="medium"
                    title={translateKey('desktopProcess.showDesktopProcessContent')}
                >
                    <HorizontalFormGroup hasErrors={validationErrors.has('reasonForAccess')}>
                        <FormLabel>{translateKey('desktopProcessAccessReason.reasonForAccess')}</FormLabel>
                        <InputContainer>
                            <NullableInputText
                                error={validationErrors.get('reasonForAccess')}
                                onChange={async (value) => await handleChange(value)}
                                onDoValidate={async () => await validateOne('reasonForAccess')}
                                placeholder={translateKey('desktopProcessAccessReason.reasonForAccess')}
                                value={reasonForAccess}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                </Modal>
            )}
        </>
    );

    async function handleChange(value: string | null) {
        setReasonForAccess(value);
    }

    async function validateOne(name: string) {
        setValidationErrors(
            updateValidationErrorsForOne(desktopProcessAccessReasonSchema, { reasonForAccess }, validationErrors, name)
        );
    }

    async function onSubmit() {
        const { errors, item } = validate(desktopProcessAccessReasonSchema, {
            reasonForAccess,
            organizationId: props.organizationId,
        });
        await setValidationErrors(errors);
        if (errors.size > 0) {
            return;
        }

        setIsSaving(true);
        const url = '/api/desktopProcessAccessReasons';
        await fetchJson(url, 'POST', item);
        await props.onReasonGiven();
    }
}
